import { UseQueryOptions, UseQueryResult, useQuery } from "@tanstack/react-query";
import { IPaginatedApiResponse } from "../../common/DataSelect/dataselector.interface";
import { IGetActionParams, IQueryParams, getAction, workflowQueryKeys } from "../actions/sector-workflow.actions";
import { IWorkflowBaseRecord, IWorkflowNodeConfig } from "../interfaces";

export interface IUseWorkflowQueryParams<TResponse> extends IGetActionParams {
  queryKey: readonly unknown[];
  enabled?: boolean;
  staleTime?: number;
  queryOptions?: Omit<UseQueryOptions<TResponse, Error>, "queryKey" | "queryFn" | "enabled" | "staleTime">;
}

export const useWorkflowQuery = <TResponse>(params: IUseWorkflowQueryParams<TResponse>): UseQueryResult<TResponse, Error> => {
  const { queryKey, uri, queryParams, config, enabled = true, staleTime, queryOptions } = params;

  return useQuery<TResponse, Error>({
    queryKey,
    queryFn: () =>
      getAction<TResponse>({
        uri,
        queryParams,
        config,
      }),
    enabled,
    staleTime,
    ...queryOptions,
  });
};

export interface IUseCatalogRecordsParams {
  selectedNode: IWorkflowNodeConfig;
  queryParams?: IQueryParams;
  enabled?: boolean;
}

export interface IUseCatalogRecordsReturn {
  records: IWorkflowBaseRecord[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
}

export const useCatalogRecords = (params: IUseCatalogRecordsParams): IUseCatalogRecordsReturn => {
  const { selectedNode, queryParams, enabled = true } = params;

  const query = useWorkflowQuery<IPaginatedApiResponse<IWorkflowBaseRecord>>({
    queryKey: workflowQueryKeys.catalogRecords(selectedNode.requestConfig?.uri ?? "", queryParams),
    uri: selectedNode.requestConfig?.uri ?? "",
    queryParams,
    enabled: enabled && Boolean(selectedNode.requestConfig?.uri),
  });

  return {
    records: query.data?.items ?? [],
    page: query.data?.page ?? 1,
    pageSize: query.data?.pageSize ?? 10,
    totalItems: query.data?.totalItems ?? 0,
    totalPages: query.data?.totalPages ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error ?? null,
    refetch: () => {
      void query.refetch();
    },
  };
};
